import { Component, OnDestroy, OnInit } from '@angular/core';
import { ContainsSubscriptions } from '../../../core/services/interfaces/contains-subscriptions';
import { Subscription } from 'rxjs';
import { unsubscribeFromAll } from '../../../core/pure-functions/rx-helper';
import { ActivatedRoute, Router } from '@angular/router';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { CustomerReviewDAO } from '../../../core/services/dao/customerReviewDAO';
import { CharterDAO } from '../../../core/services/dao/charterDAO';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import { getPossessiveName } from 'anchor-shared/lib/service/utils/helper';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomerReviewRatingType } from 'anchor-shared/lib/model/enum/customer-review-rating-type';
import { CustomerReview } from 'anchor-shared/lib/model/entity/customerReview';
import { CustomerReviewStatus } from 'anchor-shared/lib/model/enum/customer-review-status';

// NOTE: each review step can only have one prompt of type 'rating'
const buildBaseCustomerReviewSteps = (charter) => [
  {
    type: CustomerReviewRatingType.Overall,
    title: 'How was your charter?',
    icon: '/prod/yacht.svg',
    prompts: [
      {
        reviewPrompt: `Let us know how your overall experience was with ${charter.boat.owner.firstName} and their boat.`,
        type: 'rating',
      },
    ],
  },
  {
    type: CustomerReviewRatingType.Cleanliness,
    title: `How clean was ${
      getPossessiveName(charter.boat.owner.firstName) || 'the'
    } boat?`,
    icon: '/prod/spray_bottle.svg',
    prompts: [
      {
        reviewPrompt: 'How well did they clean their boat before you arrived?',
        type: 'rating',
      },
    ],
  },
  {
    type: CustomerReviewRatingType.Amenities,
    title: `How were the amenities of ${
      getPossessiveName(charter.boat.owner.firstName) || 'the'
    } boat?`,
    icon: '/prod/unicorn.svg',
    prompts: [
      {
        reviewPrompt:
          'Was the boat stocked with water toys and household items for your group to enjoy?',
        type: 'rating',
      },
    ],
  },
  {
    type: CustomerReviewRatingType.Accuracy,
    title: `How accurately did ${
      charter.boat.owner.firstName || 'the owner'
    } describe their boat?`,
    icon: '/prod/check_outline.svg',
    prompts: [
      {
        reviewPrompt: `Did ${
          getPossessiveName(charter.boat.owner.firstName) || 'the'
        } boat meet your expectations based on the listing?`,
        type: 'rating',
      },
    ],
  },
  {
    type: CustomerReviewRatingType.StaffPerformance,
    title: 'How did the Captain/Crew perform their duties?',
    icon: '/prod/captain.svg',
    prompts: [
      {
        reviewPrompt:
          'Was the Captain/Crew friendly and did they make your group feel safe?',
        type: 'rating',
      },
    ],
  },
  {
    type: CustomerReviewRatingType.Location,
    title: 'What did you think of the location/destination?',
    icon: '/prod/map.svg',
    prompts: [
      {
        reviewPrompt:
          'Was the departure location convenient and did you enjoy where you went?',
        type: 'rating',
      },
    ],
  },
  {
    type: CustomerReviewRatingType.Value,
    title: `Finally, was ${
      getPossessiveName(charter.boat.owner.firstName) || 'the'
    } boat worth what you paid?`,
    icon: '/prod/tag.svg',
    prompts: [
      {
        reviewPrompt: `How was the value of ${
          getPossessiveName(charter.boat.owner.firstName) || 'the'
        } boat for the price?`,
        type: 'rating',
      },
    ],
  },
  {
    type: CustomerReviewRatingType.Public,
    title: 'Write a public review',
    icon: '/prod/edit.svg',
    prompts: [
      {
        id: 'public',
        reviewPrompt: `We’ll show this feedback to other guests —after the review period ends, we\'ll publish it on ${
          getPossessiveName(charter.boat.owner.firstName) || 'the'
        } listing page.`,
        type: 'custom',
      },
    ],
  },
  {
    type: CustomerReviewRatingType.Private,
    title: 'Write a private note',
    icon: '/prod/comment.svg',
    prompts: [
      {
        id: 'private',
        reviewPrompt: `This feedback is just for ${
          charter.boat.owner.firstName || 'the owner'
        }—share what they can improve about their boat or the overall experience.`,
        type: 'custom',
      },
    ],
  },
];

@Component({
  selector: 'app-customer-review',
  templateUrl: './customer-review.component.html',
  styleUrls: ['./customer-review.component.scss'],
})
export class CustomerReviewComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  // data
  customerReview: CustomerReview;
  charterId: number;
  charter: Charter;
  subReviews: [] = [];
  // utility
  baseCustomerReviewSteps;
  step: number = 0;
  stepChange: boolean = false;
  subs: Subscription[] = [];
  isMobile: boolean;

  constructor(
    private customerReviewDAO: CustomerReviewDAO,
    private route: ActivatedRoute,
    private breakpointObserver: BreakpointObserver,
    private router: Router,
  ) {
    const breakpointSub = this.breakpointObserver
      .observe(['(min-width: 960px)'])
      .subscribe((state: BreakpointState) => {
        this.isMobile = !state.matches;
      });
    this.subs.push(breakpointSub);
    this.charterId = this.route.snapshot.params.charterId;
  }

  ngOnInit() {
    this.charter = this.route.snapshot.data.charterData;
    this.baseCustomerReviewSteps = buildBaseCustomerReviewSteps(this.charter);
    const reviewSub = this.customerReviewDAO
      .getReviewByCharterId(this.charter.id)
      .subscribe((review: CustomerReview) => {
        if (review) {
          this.customerReview = review;
        } else {
          this.customerReview = {
            charterId: this.charterId,
            vesselId: this.charter.vesselId,
            userId: this.charter.userId,
            status: CustomerReviewStatus.Incomplete,
            customerReviewCategories: [],
          };
        }
        if (review?.status === CustomerReviewStatus.Complete) {
          this.step = this.baseCustomerReviewSteps.length;
        } else {
          this.step = this.customerReview.customerReviewCategories.length;
          this.createDefaultReview();
        }
      });
    this.subs.push(reviewSub);
  }

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  // data

  createDefaultReview() {
    if (
      this.customerReview.customerReviewCategories.length <
      this.baseCustomerReviewSteps.length
    ) {
      const reviewStep = this.baseCustomerReviewSteps[this.step];
      const customReviewPrompts = reviewStep.prompts.filter(
        (x) => x.type === 'custom',
      );

      const ratingDetails = {};
      customReviewPrompts.forEach((x) => (ratingDetails[x.id] = ''));
      const rating =
        customReviewPrompts.length === reviewStep.prompts.length ? null : 0;

      this.customerReview.customerReviewCategories.push({
        ratingType: reviewStep.type,
        rating,
        ratingDetails,
      });
    }
  }

  submit(review: CustomerReview) {
    let completeReview = false;
    let reviewToSave = review;
    if (this.step + 1 === this.baseCustomerReviewSteps.length) {
      // set reviews to complete
      completeReview = true;
      reviewToSave.status = CustomerReviewStatus.Complete;
    } else {
      reviewToSave.customerReviewCategories = [
        reviewToSave.customerReviewCategories[this.step],
      ];
    }

    const createUpdateReviewSub = this.customerReviewDAO
      .createOrUpdateCharterReview(reviewToSave)
      .subscribe((review) => {
        if (review) {
          this.customerReview = review;
        }
        this.step++;
        if (this.step < this.baseCustomerReviewSteps.length) {
          this.createDefaultReview();
        }
      });
    this.subs.push(createUpdateReviewSub);
  }

  // utility
  incrementStep() {
    if (this.step + 1 === this.baseCustomerReviewSteps.length) {
      // return on last review step
      this.submit(this.customerReview);
      return;
    }

    const ratingPromptIdx = this.baseCustomerReviewSteps[
      this.step
    ].prompts.findIndex((x) => x.type === 'rating');
    const reviewStep = this.getCustomerReviewStep();

    if (!reviewStep || (ratingPromptIdx >= 0 && reviewStep.rating === 0)) {
      // set rating to 5 stars if no selected rating, then auto increment step
      this.setRating(5, true);
      return;
    }

    // auto save review on step change
    this.submit(this.customerReview);
  }

  decrementStep() {
    this.step--;
  }

  async setRating(rating: number, increment = false) {
    const reviewStep = this.getCustomerReviewStep();
    reviewStep.rating = rating;

    if (increment) {
      // auto increment step on rating selection
      setTimeout(() => this.incrementStep(), 500);
    }
  }

  handleCustomInput(promptId: string, event) {
    const reviewStep = this.getCustomerReviewStep();
    reviewStep.ratingDetails[promptId] = event.target.value;
  }

  getStepProgress() {
    const stepIncrements = 100 / this.baseCustomerReviewSteps.length;
    return stepIncrements * (this.step + 1) - stepIncrements;
  }

  private getStepType() {
    return this.baseCustomerReviewSteps[this.step].type;
  }

  getCustomerReviewStep() {
    const type = this.getStepType();
    return this.customerReview.customerReviewCategories.find(
      (x) => x.ratingType === type,
    );
  }

  navigateHome() {
    this.router.navigate(['/']);
  }

  navigateToCharters() {
    this.router.navigate(['/customer/charters'], {
      queryParams: { activeTab: 'Past' },
    });
  }
}
